import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

/**
 * Modal de leitura de Termos / Privacidade (HTML sanitizado).
 * Largura responsiva: ocupa quase toda a viewport em mobile e até ~72rem em desktop.
 */
export default function LegalDocumentModal({ open, onClose, legal }) {
  const { t } = useTranslation(['profile']);
  const title = open === 'terms' ? t('profile:footerTerms') : open === 'privacy' ? t('profile:footerPrivacy') : '';
  const body = open === 'terms' ? legal?.terms : open === 'privacy' ? legal?.privacy : '';

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const sanitizedHtml = useMemo(() => {
    const raw = (body || '').trim();
    if (!raw || !/<[a-z][\s\S]*>/i.test(raw)) return null;
    return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
  }, [body]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-3 sm:p-4 md:p-6" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title">
      <button
        type="button"
        className="absolute inset-0 z-0 cursor-default border-0 bg-black/50 p-0"
        aria-label={t('profile:legalClose')}
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[min(92vh,920px)] w-full max-w-[min(100vw-1.5rem,72rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-slate-800">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-600 sm:px-6 sm:py-4">
          <h3 id="legal-modal-title" className="m-0 pr-2 text-base font-bold text-slate-800 dark:text-slate-100 sm:text-lg">
            {title}
          </h3>
          <button
            type="button"
            className="shrink-0 border-none bg-transparent text-2xl leading-none text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
            onClick={onClose}
            aria-label={t('profile:legalClose')}
          >
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-8 sm:py-6">
          {body?.trim() ? (
            sanitizedHtml ? (
              <div
                className="legal-rendered-html max-w-none text-sm leading-relaxed text-slate-700 dark:text-slate-300 sm:text-[15px] sm:leading-[1.65] [&_a]:break-words [&_a]:text-[#0546ed] [&_a]:underline dark:[&_a]:text-blue-400 [&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_h1]:mb-3 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-left [&_td]:border [&_td]:border-slate-200 [&_td]:p-2 [&_td]:align-top dark:[&_td]:border-slate-600 [&_th]:border [&_th]:border-slate-200 [&_th]:bg-slate-50 [&_th]:p-2 [&_th]:font-semibold dark:[&_th]:border-slate-600 dark:[&_th]:bg-slate-700/50 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            ) : (
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-300 sm:text-[15px]">{body}</div>
            )
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile:legalEmpty')}</p>
          )}
        </div>
        <div className="shrink-0 border-t border-slate-100 px-4 py-3 dark:border-slate-700 sm:px-6">
          <button
            type="button"
            className="w-full rounded-lg bg-[#0546ed] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#003cd3] sm:w-auto"
            onClick={onClose}
          >
            {t('profile:legalClose')}
          </button>
        </div>
      </div>
    </div>
  );
}
